import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createClient, updateClient, deleteClient, extractErrorMessage } from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2, Loader2, Search } from "lucide-react";
import { CountrySelect } from "../components/shared/CountrySelect";

export default function ClientsPage() {
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // EVENTSFLO-CLIENT-LIST-ACTIVE-CLIENTS-FILTER-V1
  // Filter mode drives whether the list is scoped to Clients that
  // currently have at least one non-terminal Project ("active") or
  // shows every Client the tenant can see ("all"). Backend does the
  // actual scoping via `?active_only=true`.
  const [clientFilter, setClientFilter] = useState("all"); // "all" | "active"
  
  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const PAGE_SIZE = 100;
  
  const [formData, setFormData] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
    billing_address: "",
    // EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1
    // EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1:
    // Structured address for multi-line PDF display. `billing_country` is
    // the existing ISO-2 country code field on the Client model — now
    // exposed in the form as the required Country input.
    address_line_1: "",
    address_line_2: "",
    postcode: "",
    town_city: "",
    billing_country: "",
    status: "active",
  });

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadClients(1, false);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timer);
    // EVENTSFLO-CLIENT-LIST-ACTIVE-CLIENTS-FILTER-V1: refetch when the
    // filter mode changes so switching between All/Active hits the
    // backend once and paging state resets to page 1.
  }, [searchTerm, sortField, sortDirection, clientFilter]);

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Use unified /api/v2/clients endpoint
  const loadClients = async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      }
      
      // Build query params for unified endpoint
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // EVENTSFLO-CLIENT-LIST-ACTIVE-CLIENTS-FILTER-V1
      // When the Active-Clients filter is on, ask the backend to
      // pre-restrict to clients with at least one non-terminal
      // Project (status not in {completed, cancelled}).
      if (clientFilter === 'active') {
        params.append('active_only', 'true');
      }

      params.append('sort', `${sortField}:${sortDirection}`);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('eventsflo_auth_token');
      
      const response = await fetch(`${API_URL}/api/v2/clients?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json());
      
      // Handle unified response format
      const clientsData = response.data || [];
      const meta = response.meta || { page: 1, limit: PAGE_SIZE, total: clientsData.length, total_pages: 1 };
      
      if (append) {
        setClients(prev => [...prev, ...clientsData]);
      } else {
        setClients(clientsData);
      }
      
      setTotalCount(meta.total);
      setHasMore(meta.page < meta.total_pages);
      setCurrentPage(meta.page);
    } catch (err) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sort handler
  const handleServerSort = (field, direction) => {
    if (direction === null) {
      setSortField("name");
      setSortDirection("asc");
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: REMOVED local filtering
  // All filtering is now done server-side via /api/v2/clients endpoint

  const handleOpenDialog = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        name: client.name,
        contact_email: client.contact_email,
        contact_phone: client.contact_phone || "",
        billing_address: client.billing_address || "",
        address_line_1: client.address_line_1 || "",
        address_line_2: client.address_line_2 || "",
        postcode: client.postcode || "",
        town_city: client.town_city || "",
        billing_country: client.billing_country || "",
        status: client.status,
      });
    } else {
      setSelectedClient(null);
      setFormData({
        name: "",
        contact_email: "",
        contact_phone: "",
        billing_address: "",
        address_line_1: "",
        address_line_2: "",
        postcode: "",
        town_city: "",
        billing_country: "",
        status: "active",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selectedClient) {
        await updateClient(selectedClient.id, formData);
        toast.success("Client updated successfully");
      } else {
        await createClient(formData);
        toast.success("Client created successfully");
      }
      setDialogOpen(false);
      loadClients();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(selectedClient.id);
      toast.success("Client deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      loadClients();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Add sortable flags
  const columns = [
    { 
      key: "name", 
      label: "Name",
      sortable: true,
      render: (value, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${row.id}`);
          }}
          className="text-left font-medium text-primary hover:underline focus:outline-none"
          data-testid={`client-name-link-${row.id}`}
        >
          {value}
        </button>
      ),
    },
    { key: "contact_email", label: "Email", sortable: true, sortKey: "email" },
    { key: "contact_phone", label: "Phone" },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            data-testid={`edit-client-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedClient(row);
              setDeleteDialogOpen(true);
            }}
            data-testid={`delete-client-${row.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading && clients.length === 0 && !searchTerm) {
    // EVENTSFLO-CLIENTS-SEARCH-INPUT-FOCUS-STABILITY-FIX-V1
    // Only show the full-page spinner on the INITIAL load (no data yet
    // AND no active search term). Subsequent debounced search reloads
    // must keep the search <Input> mounted so it doesn't lose focus
    // after every keystroke. The "X of Y clients" counter and the table
    // body act as the inline loading affordance during refetches.
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clients"
        subtitle="Manage client organizations"
        actions={
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
            data-testid="add-client-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        }
      />

      {/* Search + Filter */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="client-search"
          />
        </div>
        {/* EVENTSFLO-CLIENT-LIST-ACTIVE-CLIENTS-FILTER-V1 */}
        <div
          className="inline-flex items-center rounded-sm border border-white/10 bg-surface-elevated/30 p-0.5"
          data-testid="client-filter-toggle"
        >
          <button
            type="button"
            onClick={() => setClientFilter("all")}
            className={`px-3 py-1.5 text-xs font-medium tracking-wide uppercase rounded-sm transition-colors ${
              clientFilter === "all"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="client-filter-all"
            aria-pressed={clientFilter === "all"}
          >
            All Clients
          </button>
          <button
            type="button"
            onClick={() => setClientFilter("active")}
            className={`px-3 py-1.5 text-xs font-medium tracking-wide uppercase rounded-sm transition-colors ${
              clientFilter === "active"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="client-filter-active"
            aria-pressed={clientFilter === "active"}
          >
            Active Clients
          </button>
        </div>
        <div className="text-sm text-muted-foreground" data-testid="client-list-count">
          {clients.length} of {totalCount}{" "}
          {clientFilter === "active" ? "active " : ""}clients
          {hasMore && <span className="text-xs ml-1">(more available)</span>}
        </div>
      </div>

      <div className="bg-card border border-white/5 rounded-sm">
        {clients.length === 0 && !searchTerm && clientFilter !== "active" ? (
          <EmptyState
            icon={Users}
            title="No clients yet"
            description="Create your first client to get started with projects and shifts."
            action={
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground"
                data-testid="empty-add-client-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            }
          />
        ) : clients.length === 0 ? (
          <div
            className="py-12 text-center text-muted-foreground"
            data-testid="clients-empty-state"
          >
            <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
            {/* EVENTSFLO-CLIENT-LIST-ACTIVE-CLIENTS-FILTER-V1 */}
            <p>
              {clientFilter === "active" && searchTerm
                ? "No active Clients match your search."
                : clientFilter === "active"
                  ? "No active Clients found."
                  : "No clients match your search criteria"}
            </p>
          </div>
        ) : (
          <>
            {/* EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sorting */}
            <DataTable 
              columns={columns} 
              data={clients}
              onSort={handleServerSort}
              sortField={sortField}
              sortDirection={sortDirection}
              disableLocalSort={true}
            />
            {hasMore && (
              <div className="p-4 border-t border-white/5 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadClients(currentPage + 1, true)}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedClient ? "Edit Client" : "Add Client"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Client name"
                required
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label htmlFor="contact_email">Email *</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="contact@example.com"
                required
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label htmlFor="contact_phone">Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="+44 123 456 7890"
                data-testid="client-phone-input"
              />
            </div>
            {/* EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1:
                Structured Billing Address section. Legacy `billing_address`
                single-string is retained below inside a collapsible details
                block so it does not visually dominate the form. */}
            <div className="rounded-md border border-border/60 p-3 space-y-3">
              <div className="text-sm font-semibold text-foreground">Billing Address</div>
              <div>
                <Label htmlFor="address_line_1">Address Line 1</Label>
                <Input
                  id="address_line_1"
                  value={formData.address_line_1}
                  onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                  placeholder="Street / building"
                  data-testid="client-address-line-1-input"
                />
              </div>
              <div>
                <Label htmlFor="address_line_2">Address Line 2</Label>
                <Input
                  id="address_line_2"
                  value={formData.address_line_2}
                  onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                  placeholder="Optional"
                  data-testid="client-address-line-2-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    data-testid="client-postcode-input"
                  />
                </div>
                <div>
                  <Label htmlFor="town_city">City</Label>
                  <Input
                    id="town_city"
                    value={formData.town_city}
                    onChange={(e) => setFormData({ ...formData, town_city: e.target.value })}
                    data-testid="client-town-city-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="billing_country">Country</Label>
                {/* EVENTSFLO-UNIVERSAL-COUNTRY-DROPDOWN-AND-VENUE-ADDRESS-FIELDS-V1 */}
                <CountrySelect
                  value={formData.billing_country}
                  onValueChange={(v) =>
                    setFormData({ ...formData, billing_country: (v || "").toUpperCase().slice(0, 2) })
                  }
                  valueMode="iso2"
                  placeholder="Search country..."
                  testId="client-country-input"
                />
              </div>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none py-1 hover:text-foreground">
                  Advanced / legacy single-line address
                </summary>
                <div className="pt-2">
                  <Label htmlFor="billing_address" className="text-xs">
                    Legacy address (used only when structured fields above are empty)
                  </Label>
                  <Input
                    id="billing_address"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    placeholder="Legacy single-line address (backward compatibility)"
                    data-testid="client-address-input"
                  />
                </div>
              </details>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="client-submit-btn" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedClient ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Client"
        itemName={selectedClient?.name}
        itemType="client"
        description={`Are you sure you want to delete client "${selectedClient?.name}"? This may affect associated projects and invoices. This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
